import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {GridDataResult} from '@progress/kendo-angular-grid';
import {State, toODataString} from '@progress/kendo-data-query';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ErrorService} from '../../pages/shared/errors/error.service';
import {DateHelper} from '../utils/date-helper';

/**
 * Provides OData-backed queries for dropdown lists with Kendo Grid integration.
 *
 * Extends `BehaviorSubject<GridDataResult>` so consumers can subscribe to data
 * updates directly. Handles `$count` aggregation and converts ISO date strings
 * into `Date` objects using `DateHelper`.
 */
@Injectable()
export class DropdownListService extends BehaviorSubject<GridDataResult> {

  /** Indicates whether a data request is in-flight. */
  public loading: boolean = false;

  /** Base API URL for v1 OData endpoints. */
  protected BASE_ODATA_URL = `${environment.apiBaseUrl}/api/v1/`;

  /**
   * Creates the service.
   * @param http Angular HTTP client used for API requests.
   * @param errorService Centralized error handling service.
   */
  constructor(
    private http: HttpClient,
    protected errorService: ErrorService,
  ) {
    super(null);
  }

  /**
   * Builds the OData URL segment for a specific entity by id, if provided.
   * @param data An object that may contain an `id` property.
   * @returns A string like `"/123"` when id exists, otherwise an empty string.
   */
  getIdUrlPart(data: any): string {
    if (data == null) {
      return '';
    }
    return `/${data.id}`;
  }

  /**
   * Executes an OData query and emits results to subscribers.
   * @param tableName Target OData entity set name.
   * @param state Kendo Data Query state (paging, sorting, filtering).
   * @param params Additional query parameters to append to the request.
   */
  public query(tableName: string, state: any, params = {}): void {
    this.fetch(tableName, state, null, params)
      .subscribe((x) => super.next(x));
  }

  /**
   * Performs the HTTP request to fetch OData results.
   * Converts `value` entries' date-like string fields into `Date` objects.
   * @param tableName Target OData entity set name.
   * @param state Kendo Data Query state or OData state object.
   * @param data Optional entity used to generate an id segment.
   * @param params Additional query parameters.
   * @returns Observable that yields a Kendo `GridDataResult`.
   */
  protected fetch(tableName: string, state: any | State, data: any = null, params = {}): Observable<GridDataResult> {
    const queryStr = `${this.getIdUrlPart(data)}${toODataString(state)}`;
    this.loading = true;

    const newParams = Object.assign({}, params);
    newParams['$count'] = true;
    return this.http
      .get(`${this.BASE_ODATA_URL}${tableName}?${queryStr}`, {params: newParams})
      .pipe(
        map((response) => ({
          data: response['value'].map((e) => DateHelper.ConvertObjectStringAllDateKeysToDateObjects(e)),
          total: parseInt(response['@odata.count'], 10),
        } as GridDataResult)),
        tap(() => this.loading = false),
      );
  }

}
